import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Board, BoardMessage } from '../../models/board.model';
import { BoardEditorSessionService } from '../../services/board-editor-session.service';
import { BoardApiService } from '../../services/board-api.service';
import { accentInk, messageGrid, overflows, TILE_COLORS } from '../../utils/message-layout';

@Component({
  selector: 'app-board-editor',
  imports: [FormsModule, RouterLink],
  templateUrl: './board-editor.html',
  styleUrl: './board-editor.css',
  host: { '(window:keydown)': 'onUndo($event)' },
})
export class BoardEditor implements OnInit, OnDestroy {
  private sessions = inject(BoardEditorSessionService);
  private api = inject(BoardApiService);
  private routeId = inject(ActivatedRoute).snapshot.paramMap.get('id');
  protected session = this.sessions.open(this.routeId);
  protected board = this.session.board;
  protected selected = this.session.selected;
  protected colors = TILE_COLORS;
  protected alignments = ['left', 'center', 'right'] as const;
  protected nowShowing = signal<string | null>(null);
  protected expiryOpen = signal(false);
  protected passcodeOpen = signal(false);
  protected settingError = signal('');
  protected draggedId: string | null = null;
  protected textarea = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');
  private poll?: ReturnType<typeof setInterval>;
  private stateRequest?: Subscription;
  private observedAt = 0;
  protected grid = computed(() =>
    messageGrid(
      this.selected(),
      this.board()?.config.rows || 3,
      this.board()?.config.columns || 24,
    ),
  );
  protected overflow = computed(() =>
    overflows(
      this.selected()?.text || '',
      this.board()?.config.rows || 3,
      this.board()?.config.columns || 24,
    ),
  );
  protected ink = accentInk;

  constructor() {
    // Once /boards/new is created, point the URL at it so a reload reopens it instead of creating another.
    const location = inject(Location);
    effect(() => {
      const id = this.board()?.id;
      if (!this.routeId && id) location.replaceState(`/boards/${id}/edit`);
    });
  }

  ngOnInit(): void {
    this.poll = setInterval(() => this.pollDisplay(), 3000);
    this.pollDisplay();
  }
  ngOnDestroy(): void {
    clearInterval(this.poll);
    this.stateRequest?.unsubscribe();
  }
  private pollDisplay(): void {
    if (Date.now() - this.observedAt > 15000) this.nowShowing.set(null);
    const id = this.board()?.id;
    if (!id || (this.stateRequest && !this.stateRequest.closed)) return;
    this.stateRequest = this.api.getDisplayState(id).subscribe({
      next: (state) => {
        this.nowShowing.set(state.message_id);
        this.observedAt = Date.now();
      },
      error: () => this.nowShowing.set(null),
    });
  }

  protected change(update: (board: Board) => Board, debounce = false): void {
    this.sessions.change(this.session, update, debounce);
  }
  protected messageChange(values: Partial<BoardMessage>, debounce = false): void {
    const id = this.session.selectedId();
    this.change(
      (board) => ({
        ...board,
        messages: board.messages.map((m) => (m.id === id ? { ...m, ...values } : m)),
      }),
      debounce,
    );
  }
  protected textChange(text: string): void {
    this.messageChange({ text }, true);
  }
  protected setAlign(align: 'left' | 'center' | 'right'): void {
    this.messageChange({ align });
  }
  protected setColor(color: string): void {
    this.messageChange({ accentColor: color === TILE_COLORS[0] ? null : color });
  }
  protected setName(name: string): void {
    this.change((board) => ({ ...board, name }), true);
  }
  protected setTitle(title: string): void {
    this.change((board) => ({ ...board, title }), true);
  }
  protected setPasscode(passcode: string): void {
    if (!passcode) {
      this.settingError.set(
        'A passcode of at least 4 characters is required for public display access.',
      );
      return;
    }
    this.settingError.set('');
    this.change((board) => ({ ...board, passcode }), true);
  }
  protected toggleSound(): void {
    this.change((board) => ({ ...board, soundEnabled: !(board.soundEnabled ?? true) }));
  }
  protected setRotation(event: Event): void {
    // Commit on change, not per keystroke: typing 61 must not save its valid prefix 6.
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);
    if (!Number.isInteger(value) || value < 5 || value > 60) {
      this.settingError.set('Rotation must be 5–60 seconds.');
      input.value = String(this.board()!.config.rotationInterval);
      return;
    }
    this.settingError.set('');
    this.change((board) => ({ ...board, config: { ...board.config, rotationInterval: value } }));
  }
  protected resize(dimension: 'rows' | 'columns', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);
    const board = this.board()!;
    const min = dimension === 'rows' ? 2 : 10;
    const max = dimension === 'rows' ? 10 : 60;
    if (!Number.isInteger(value) || value < min || value > max) {
      this.settingError.set(`${dimension === 'rows' ? 'Rows' : 'Columns'} must be ${min}–${max}.`);
      input.value = String(board.config[dimension]);
      return;
    }
    const config = { ...board.config, [dimension]: value };
    if (
      value < board.config[dimension] &&
      board.messages.some((m) => overflows(m.text, config.rows, config.columns)) &&
      !window.confirm(
        'Some message text will not fit at this size. Keep the full text and clip it on the display?',
      )
    ) {
      input.value = String(board.config[dimension]);
      return;
    }
    this.settingError.set('');
    this.change((board) => ({ ...board, config }));
  }
  protected select(id: string): void {
    this.session.selectedId.set(id);
    this.expiryOpen.set(false);
  }
  protected addMessage(): void {
    const id = crypto.randomUUID();
    this.change((board) => ({
      ...board,
      messages: [
        ...board.messages,
        {
          id,
          text: '',
          align: 'left',
          accentColor: null,
          layout: 'lines',
          order: board.messages.length,
        },
      ],
    }));
    this.select(id);
    setTimeout(() => this.textarea()?.nativeElement.focus());
  }
  protected duplicate(id: string): void {
    const copyId = crypto.randomUUID();
    this.change((board) => {
      const index = board.messages.findIndex((m) => m.id === id);
      board.messages.splice(index + 1, 0, { ...board.messages[index], id: copyId });
      board.messages.forEach((m, order) => (m.order = order));
      return board;
    });
    this.select(copyId);
  }
  protected remove(id: string): void {
    this.change((board) => ({
      ...board,
      messages: board.messages.filter((m) => m.id !== id).map((m, order) => ({ ...m, order })),
    }));
    if (id === this.session.selectedId()) this.select(this.board()?.messages[0]?.id || '');
  }
  protected dragStart(id: string, event: DragEvent): void {
    this.draggedId = id;
    event.dataTransfer?.setData('text/plain', id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }
  protected drop(target: string, event: DragEvent): void {
    event.preventDefault();
    if (this.draggedId)
      this.move(
        this.draggedId,
        this.board()!.messages.findIndex((m) => m.id === target),
      );
    this.draggedId = null;
  }
  protected reorderKey(id: string, event: KeyboardEvent): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const index = this.board()!.messages.findIndex((m) => m.id === id);
    this.move(id, index + (event.key === 'ArrowUp' ? -1 : 1));
  }
  private move(id: string, index: number): void {
    this.change((board) => {
      const from = board.messages.findIndex((m) => m.id === id);
      if (index < 0 || index >= board.messages.length || from < 0) return board;
      board.messages.splice(index, 0, board.messages.splice(from, 1)[0]);
      board.messages.forEach((m, order) => (m.order = order));
      return board;
    });
  }
  protected expiryValue(): string {
    const value = this.selected()?.expiresAt;
    if (!value) return '';
    const date = new Date(value);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
  protected setExpiry(value: string): void {
    const date = value ? new Date(value) : undefined;
    if (date && Number.isNaN(date.getTime())) return;
    this.messageChange({ expiresAt: date?.toISOString() });
  }
  protected expiryLabel(message?: BoardMessage): string {
    if (!message?.expiresAt) return 'never';
    return new Date(message.expiresAt).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  protected meta(message: BoardMessage): string {
    if (message.expiresAt && new Date(message.expiresAt).getTime() <= Date.now())
      return `expired ${this.expiryLabel(message)}`;
    if (message.id === this.nowShowing())
      return `showing now · ${this.board()?.config.rotationInterval || 10}s`;
    return message.expiresAt ? `until ${this.expiryLabel(message)}` : 'always';
  }
  protected messageName(message?: BoardMessage): string {
    return message?.text.split('\n')[0].trim() || 'New message';
  }
  protected queueText(message: BoardMessage): string {
    return (
      message.text.toUpperCase().split('\n').slice(0, this.board()?.config.rows).join('\n') ||
      'New message'
    );
  }
  protected retry(): void {
    this.sessions.retry(this.session);
  }
  protected onUndo(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.sessions.undo(this.session);
    }
  }
}
